import React, { useState } from 'react'
import { Modal, Form, Col, Row, Button, ListGroup, InputGroup, CloseButton } from 'react-bootstrap'
import { createModal, ModalResolveType, useModalAction } from '@src/lib/modal-manager'
import { useForm } from '@src/lib/react-hook-form'
import z, { object } from 'zod'
import { isEmpty } from '@src/lib/lodash'

interface EditPioProps {
  dtoUuid?: string;
  itemDto?: PioItemDto;
}

interface pioArgItem {
  description: string;
}

interface PioItemDto {
  code?: string;
  description: string;
  arguments: pioArgItem[];
  status: string;
}

const initState: PioItemDto = {
  code: '',
  description: '',
  arguments: [],
  status: '1'
}

export function EditPioModalWrapper(props: EditPioProps) {
  const { resolve, remove, hide } = useModalAction()
  const { dtoUuid, itemDto } = props
  const defaultArgs = itemDto?.arguments || [] as pioArgItem[]
  const [argFieldValue, setArgFieldValue] = useState('')
  const [args, setArgs] = useState<pioArgItem[]>(defaultArgs)
  
  const { register, handleSubmit, setValue, formState: { errors } } = useForm<PioItemDto>({
    reValidateMode: 'onSubmit',
    defaultValues: {...initState, ...itemDto},
    rules: z.object({
      code: z.string().optional(),
      description: z.string().min(1, '項目名稱是必填的'),
      arguments: z.array(object({ description: z.string() })),
      status: z.string().min(1, {message: '請選擇狀態'})
    })
  })

  const addAge = (description: string) => {
    if (isEmpty(description)) return
    const newAge = { description }
    setArgs([...args, newAge]);
    setValue('arguments', [...args, newAge])
    setArgFieldValue('')
  }

  const removeAge = (index: number) => {
    const newargs = args.filter((_, i) => i !== index);
    setArgs(newargs);
    setValue('arguments', newargs)
  }

  const handleClose = () => {
    resolve(ModalResolveType.CLOSED)
    hide()
    remove()
  }

  const handleCancel = () => {
    resolve(ModalResolveType.CANCEL)
    hide()
    remove()
  }

  const handleOnSubmit = (formDto: PioItemDto) => {
    resolve(ModalResolveType.OK, formDto)
    hide()
    remove()
  }

  return (
    <Modal size='lg' show onHide={handleClose} centered>
      <Form onSubmit={handleSubmit(handleOnSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>{`${dtoUuid ? '編輯' : '新增'}項目(PIO)`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column sm='2'>
              項目名稱
            </Form.Label>
            <Col sm='10'>
              <Form.Control placeholder='車輛通訊盒STMB-3000' { ...register('description') } />
            </Col>
            {errors.description && <Form.Text className='text-danger'>{errors.description.message}</Form.Text>}
          </Form.Group>
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column sm='2'>
              項目相關參數
            </Form.Label>
            <Col sm='10'>
              <InputGroup>
                <Form.Control placeholder='參數名稱' value={argFieldValue} onChange={e => setArgFieldValue(e.target.value)} />
                <Button variant='outline-primary' onClick={() => addAge(argFieldValue)}>
                  新增
                </Button>
              </InputGroup>
            </Col>
            {errors.arguments && <Form.Text className='text-danger'>{errors.arguments.message}</Form.Text>}
          </Form.Group>
          <ListGroup className='mb-3'>
            {args.map((param, index) => (
              <ListGroup.Item key={index} style={{ border: 'none' }}>
                {param.description}
                <CloseButton style={{ float: 'right' }} onClick={() => removeAge(index)} />
              </ListGroup.Item>
            ))}
          </ListGroup>
          
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column sm='2'>
              狀態
            </Form.Label>
            <Col sm='10'>
              <Form.Select {...register('status')}>
                <option value='1'>啟用</option>
                <option value='0'>停用</option>
              </Form.Select>
            </Col>
            {errors.status && <Form.Text className='text-danger'>{errors.status.message}</Form.Text>}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleCancel} variant='light'>
            取消
          </Button>
          <Button type='submit'>
            確認
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export const EditPioModal = createModal(EditPioModalWrapper)
